import { useQuery } from "@tanstack/react-query"
import { Calendar, Users, Building2, BarChart } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { StatsCard } from "@/components/dashboard/StatsCard"
import { UpcomingEventsTable } from "@/components/dashboard/UpcomingEventsTable"
import { getCorporateDashboardStats } from "@/lib/queries/corporate"
import { cn } from "@/lib/utils"

// Shimmer components for loading states
const ShimmerCard = () => (
  <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
    <div className="p-6 flex flex-col space-y-2">
      <div className="h-4 w-1/3 animate-pulse rounded bg-muted"></div>
      <div className="h-8 w-1/2 animate-pulse rounded bg-muted"></div>
    </div>
  </div>
)

const ShimmerRow = () => (
  <div className="flex items-center space-x-4">
    <div className="h-12 w-12 animate-pulse rounded-full bg-muted"></div>
    <div className="space-y-2 flex-1">
      <div className="h-4 w-3/4 animate-pulse rounded bg-muted"></div>
      <div className="h-4 w-1/2 animate-pulse rounded bg-muted"></div>
    </div>
  </div>
)

export default function DashboardOverview({ profile, session }: { profile: any | null, session: any | null }) {
  const navigate = useNavigate()

  const { data: stats, isLoading } = useQuery({
    queryKey: ["corporate-dashboard", profile?.id],
    queryFn: () => getCorporateDashboardStats(session?.access_token!),
    enabled: !!profile?.id && !!session?.access_token,
  })

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {isLoading ? (
          <>
            <ShimmerCard />
            <ShimmerCard />
            <ShimmerCard />
          </>
        ) : (
          <>
            <StatsCard
              title="Total Events Booked"
              value={stats?.totalEvents || 0}
              icon={Calendar}
            />
            <StatsCard
              title="Total Participants"
              value={stats?.totalParticipants || 0}
              icon={Users}
            />
            <StatsCard
              title="Unique NPOs"
              value={stats?.uniqueNPOs || 0}
              icon={Building2}
            />
          </>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-1">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Impact Overview</CardTitle>
            <BarChart className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="relative overflow-hidden rounded-md border">
              {isLoading ? (
                <div className="h-[375px] w-full animate-pulse bg-muted"></div>
              ) : (
                <iframe 
                  width="100%" 
                  height="500" 
                  src="https://lookerstudio.google.com/embed/reporting/026811eb-f6c4-49e8-9cc5-10ef16833b3e/page/yhe1C" 
                  frameBorder="0" 
                  style={{ border: 0 }} 
                  allowFullScreen 
                  sandbox="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                ></iframe>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-1">
        <Card>
          <CardHeader>
            <CardTitle>Recently Booked Events</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                <ShimmerRow />
                <ShimmerRow />
                <ShimmerRow />
              </div>
            ) : (
              <UpcomingEventsTable events={stats?.upcomingEventsList || []} profile={profile} session={session} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}